import * as Sentry from '@sentry/react'

import { api, makeApiRequest } from './_init'
import { parseSetupProcess } from './models'

const languageMap = Object.freeze({
  en: 'English',
  de: 'German',
  fr: 'French',
  pt: 'European Portuguese',
  cs: 'Czech',
  hr: 'Croatian',
  fi: 'Finnish',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
})

export async function fetchGeneratedCreativesAsync(
  { eventId, tc, tcRunId, creativeFieldName, language, requiredNumberOfCreatives = 3 },
  controller = null,
) {
  const params = {
    event_id: eventId,
    tc,
    tc_run_id: tcRunId,
    creatives_type: creativeFieldName,
    required_number_of_creatives: requiredNumberOfCreatives,
    language: languageMap[language],
  }

  try {
    const result = await api.get('/integrations/openai/gpt-4.1/generate_creatives', {
      params,
      signal: controller?.signal,
    })

    return {
      results: result.data,
      success: result.status === 200,
    }
  } catch (e) {
    Sentry.captureException(e)
    return { success: false }
  }
}

let setupController

const setupProcessApi = {
  updateAsync: async (eventId, payload) => {
    if (setupController) {
      setupController.abort()
    } else {
      setupController = new AbortController()
    }

    const { data, error, status } = await makeApiRequest(
      api,
      'put',
      `/setup_processes/${eventId}`,
      payload,
      { signal: setupController.signal },
    )
    if (status === 200) {
      return { data: parseSetupProcess(data, eventId), error, status }
    }

    return { error, status, data }
  },
  fetchAsync: async (eventId) => {
    const { data, error, status } = await makeApiRequest(
      api,
      'get',
      `/setup_processes/${eventId}`,
      null,
    )
    if (status === 200) {
      return { data: parseSetupProcess(data, eventId), error, status }
    }

    return { error, status }
  },
  createAsync: async (
    eventId,
    {
      goal,
      conversion_id,
      lead_form_id,
      custom_event_type,
      total_budget,
      start_date,
      end_date,
      creatives,
      integration_details,
      targeting = [],
    },
  ) => {
    const { data, error, status } = await makeApiRequest(
      api,
      'post',
      `/setup_processes/${eventId}`,
      {
        goal,
        total_budget,
        start_date,
        end_date,
        creatives,
        integration_details,
        conversion_id: conversion_id ?? '',
        lead_form_id: lead_form_id ?? '',
        custom_event_type: custom_event_type ?? '',
        targeting,
      },
    )
    if (status === 200) {
      return { data: parseSetupProcess(data, eventId), error, status }
    }

    return { error, status, data }
  },
  deleteAsync: async (eventId, cancel) => {
    const { error, status } = await makeApiRequest(
      api,
      'delete',
      `/setup_processes/${eventId}?cancel=${cancel}`,
      null,
    )

    return { error, status }
  },
  publishAsync: async (eventId) => {
    if (setupController) {
      setupController.abort()
    }
    const { error, status } = await makeApiRequest(
      api,
      'post',
      `/setup_processes/${eventId}/boost`,
    )

    return { error, status }
  },
}

export default setupProcessApi
